/** Импорт каталога Texnomart.uz — admin-triggered bulk-импорт (не CSV).
 *
 * Владелец сам сопоставляет пары (URL категории на Texnomart → категория
 * BD, куда её импортировать) — сознательно без авто-мэтчинга по имени,
 * это надёжнее для пилота. Импортированные товары создаются скрытыми
 * (is_active=false) — включать их в каталог нужно вручную после проверки
 * (Товары → фильтр «неактивные»).
 *
 * Прогон идёт в фоне на бэкенде (может занять минуты — по товару на
 * категорию с паузами между запросами к Texnomart), эта страница только
 * показывает историю запусков с автообновлением, пока что-то ещё бежит.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus, RefreshCw, Trash2, Upload, XCircle, Zap } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PrimaryCategoryPicker } from "@/components/products/CategoryPicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface MappingRow {
  key: number;
  texnomartUrl: string;
  bdCategoryId: number | null;
}

interface AdminCategoryRaw {
  id: number;
  slug: string;
  translations: Array<{ language: string; name: string }>;
}

/** Проверено вручную (curl) на 2026-08-03 — все 4 категории реально
 * содержат товары на Texnomart на момент проверки (не все категории
 * там непустые — так и наткнулись на баг с "мини-холодильники"). Если
 * через несколько месяцев товаров там не окажется — не баг импорта,
 * просто на сайте-источнике поменялся ассортимент. bdCategoryName
 * должен точно совпадать (без учёта регистра) с RU-названием
 * категории в BD — сопоставление по имени, не по id, чтобы не
 * зависеть от конкретных id, которые могут измениться при переисеве. */
const PRESET_MAPPINGS: { label: string; texnomartUrl: string; bdCategoryName: string }[] = [
  { label: "Холодильники (73 тов.)", texnomartUrl: "/ru/katalog/vse-holodilniki/", bdCategoryName: "Холодильники" },
  { label: "Стиральные машины (51 тов.)", texnomartUrl: "/ru/katalog/stiralnye-mashinki/", bdCategoryName: "Стиральные машины" },
  { label: "Телевизоры (124 тов.)", texnomartUrl: "/ru/katalog/vse-televizory/", bdCategoryName: "Телевизоры" },
  { label: "Кондиционеры (23 тов.)", texnomartUrl: "/ru/katalog/vse-kondicionery-5/", bdCategoryName: "Кондиционеры и климатика" },
];

interface ImportRun {
  id: number;
  source: string;
  status: "pending" | "running" | "success" | "failed" | "partial";
  stats: Record<string, number>;
  error_log: {
    errors?: { category_url?: string; product_url?: string; error: string }[];
    timeout?: boolean;
    exception?: string;
    cancelled_manually?: boolean;
  };
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

const STATUS_LABEL: Record<ImportRun["status"], string> = {
  pending: "В очереди",
  running: "Выполняется",
  success: "Готово",
  failed: "Ошибка",
  partial: "Готово с ошибками",
};

const STATUS_VARIANT: Record<
  ImportRun["status"],
  "success" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  running: "secondary",
  success: "success",
  failed: "destructive",
  partial: "destructive",
};

let nextKey = 1;

export function CatalogImportPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<MappingRow[]>([
    { key: nextKey++, texnomartUrl: "", bdCategoryId: null },
  ]);
  const [selectedPresets, setSelectedPresets] = useState<Set<number>>(
    new Set(PRESET_MAPPINGS.map((_, i) => i)),
  );

  // queryKey совпадает с внутренним хуком PrimaryCategoryPicker — React
  // Query раздаёт этот запрос из общего кэша, повторного похода в API нет.
  const { data: categories } = useQuery({
    queryKey: ["admin-categories-flat"],
    queryFn: async () => {
      const { data } = await api.get<AdminCategoryRaw[]>("/categories");
      return data.map((c) => ({
        id: c.id,
        name:
          c.translations.find((t) => t.language === "ru")?.name ??
          c.translations[0]?.name ??
          c.slug,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const purgeReimportMutation = useMutation({
    mutationFn: async (preset: (typeof PRESET_MAPPINGS)[number]) => {
      const match = categories?.find(
        (c) => c.name.trim().toLowerCase() === preset.bdCategoryName.toLowerCase(),
      );
      if (!match) {
        throw new Error(`Категория «${preset.bdCategoryName}» не найдена в BD`);
      }
      const { data: purgeResult } = await api.post<{ deleted: number; skipped: number }>(
        `/catalog-import/categories/${match.id}/purge-texnomart`,
      );
      const { data } = await api.post("/catalog-import/texnomart", {
        mappings: [{ texnomart_category_url: preset.texnomartUrl, bd_category_id: match.id }],
      });
      return { purgeResult, run: data };
    },
    onSuccess: ({ purgeResult }) => {
      toast.success(
        `Удалено ${purgeResult.deleted} тов.` +
          (purgeResult.skipped > 0 ? `, пропущено ${purgeResult.skipped} (есть заказы)` : "") +
          " — импорт запущен заново",
      );
      queryClient.invalidateQueries({ queryKey: ["catalog-import-runs"] });
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.error?.message ?? e?.message ?? "Не удалось удалить и переимпортировать",
      ),
  });

  const presetMutation = useMutation({
    mutationFn: async () => {
      const chosen = PRESET_MAPPINGS.filter((_, i) => selectedPresets.has(i));
      const mappings = chosen.map((p) => {
        const match = categories?.find(
          (c) => c.name.trim().toLowerCase() === p.bdCategoryName.toLowerCase(),
        );
        if (!match) {
          throw new Error(
            `Категория «${p.bdCategoryName}» не найдена в BD — проверьте название`,
          );
        }
        return { texnomart_category_url: p.texnomartUrl, bd_category_id: match.id };
      });
      const { data } = await api.post("/catalog-import/texnomart", { mappings });
      return data;
    },
    onSuccess: () => {
      toast.success("Импорт запущен по всем выбранным категориям сразу");
      queryClient.invalidateQueries({ queryKey: ["catalog-import-runs"] });
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.error?.message ?? e?.message ?? "Не удалось запустить импорт",
      ),
  });

  function togglePreset(i: number) {
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const { data: runs, isLoading } = useQuery({
    queryKey: ["catalog-import-runs"],
    queryFn: async () => (await api.get<ImportRun[]>("/catalog-import/runs")).data,
    refetchInterval: (query) => {
      const data = query.state.data as ImportRun[] | undefined;
      const active = data?.some((r) => r.status === "pending" || r.status === "running");
      return active ? 3000 : false;
    },
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      const mappings = rows
        .filter((r) => r.texnomartUrl.trim() && r.bdCategoryId)
        .map((r) => ({
          texnomart_category_url: r.texnomartUrl.trim(),
          bd_category_id: r.bdCategoryId,
        }));
      const { data } = await api.post("/catalog-import/texnomart", { mappings });
      return data;
    },
    onSuccess: () => {
      toast.success("Импорт запущен — прогресс появится в истории ниже");
      queryClient.invalidateQueries({ queryKey: ["catalog-import-runs"] });
      setRows([{ key: nextKey++, texnomartUrl: "", bdCategoryId: null }]);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось запустить импорт"),
  });

  function updateRow(key: number, patch: Partial<MappingRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, { key: nextKey++, texnomartUrl: "", bdCategoryId: null }]);
  }

  function removeRow(key: number) {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }

  const canStart =
    rows.some((r) => r.texnomartUrl.trim() && r.bdCategoryId) && !startMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-heading flex items-center gap-2">
          <Upload className="w-6 h-6" />
          Импорт каталога Texnomart
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Товары импортируются <b>скрытыми</b> — проверьте их в разделе
          «Товары» и включите вручную перед тем, как они появятся на сайте.
        </p>
      </div>

      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            Быстрый импорт — проверенные категории
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Одной кнопкой — все категории ниже уйдут в один прогон, вручную
            ссылки вставлять не нужно.
          </p>
          <div className="space-y-2">
            {PRESET_MAPPINGS.map((p, i) => (
              <div key={p.texnomartUrl} className="flex items-center gap-2.5 text-sm">
                <label className="flex items-center gap-2.5 cursor-pointer flex-1">
                  <Checkbox
                    checked={selectedPresets.has(i)}
                    onChange={() => togglePreset(i)}
                  />
                  <span className="font-medium">{p.bdCategoryName}</span>
                  <span className="text-xs text-muted-foreground">— {p.label}</span>
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 text-muted-foreground"
                  disabled={purgeReimportMutation.isPending}
                  title="Удалить ранее импортированные товары этой категории (например, если они без картинок из-за старого бага) и импортировать заново"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Удалить все ранее импортированные товары «${p.bdCategoryName}» и импортировать заново?`,
                      )
                    ) {
                      purgeReimportMutation.mutate(p);
                    }
                  }}
                >
                  {purgeReimportMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            ))}
          </div>
          <Button
            onClick={() => presetMutation.mutate()}
            disabled={selectedPresets.size === 0 || presetMutation.isPending}
          >
            {presetMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-1" />
            )}
            Импортировать выбранное ({selectedPresets.size})
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Свой набор категорий (вручную)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {rows.map((row) => (
            <div key={row.key} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Категория на Texnomart.uz</Label>
                <Input
                  placeholder="/ru/katalog/vse-kondicionery-5/"
                  value={row.texnomartUrl}
                  onChange={(e) => updateRow(row.key, { texnomartUrl: e.target.value })}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Импортировать в категорию BD</Label>
                <PrimaryCategoryPicker
                  value={row.bdCategoryId}
                  onChange={(id) => updateRow(row.key, { bdCategoryId: id })}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeRow(row.key)}
                disabled={rows.length === 1}
              >
                <Trash2 className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>
          ))}

          <div className="flex justify-between pt-2">
            <Button variant="outline" size="sm" onClick={addRow}>
              <Plus className="w-3.5 h-3.5 mr-1" />
              Ещё категория
            </Button>
            <Button onClick={() => startMutation.mutate()} disabled={!canStart}>
              {startMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              Запустить импорт
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">История запусков</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-24" />
          ) : !runs || runs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Запусков пока не было.
            </p>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => (
                <RunRow key={run.id} run={run} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RunRow({ run }: { run: ImportRun }): JSX.Element {
  const queryClient = useQueryClient();
  const stats = run.stats ?? {};
  const errors = run.error_log?.errors ?? [];
  const isActive = run.status === "pending" || run.status === "running";

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/catalog-import/runs/${run.id}/cancel`);
    },
    onSuccess: () => {
      toast.success("Прогон помечен как отменённый");
      queryClient.invalidateQueries({ queryKey: ["catalog-import-runs"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось отменить"),
  });

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[run.status]}>{STATUS_LABEL[run.status]}</Badge>
          {isActive && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          <span className="text-xs text-muted-foreground">
            #{run.id} · {new Date(run.created_at).toLocaleString("ru-RU")}
          </span>
        </div>
        {isActive && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            title="Пометить как неудачный, если прогон завис (например, дольше 20 минут без изменений в статистике)"
          >
            {cancelMutation.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <XCircle className="w-3 h-3 mr-1" />
            )}
            Отменить
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span>Категорий: {stats.categories_done ?? 0}/{stats.categories_total ?? 0}</span>
        <span>Создано: {stats.products_created ?? 0}</span>
        <span>Пропущено (уже есть): {stats.products_skipped_existing ?? 0}</span>
        {(stats.products_failed ?? 0) > 0 && (
          <span className="text-destructive">Ошибок: {stats.products_failed}</span>
        )}
      </div>
      {run.error_log?.timeout && (
        <p className="text-xs text-destructive">
          Прервано по таймауту (55 минут) — на очень большой категории может
          не хватить одного прогона (~15-25с на товар). Запустите эти же
          категории ещё раз: уже созданные товары пропускаются мгновенно
          (без похода на Texnomart), так что повтор доберёт только
          оставшиеся и будет намного быстрее.
        </p>
      )}
      {run.error_log?.cancelled_manually && (
        <p className="text-xs text-muted-foreground">Отменено вручную.</p>
      )}
      {run.error_log?.exception && (
        <p className="text-xs text-destructive break-all">
          Ошибка: {run.error_log.exception}
        </p>
      )}
      {errors.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Показать ошибки ({errors.length})
          </summary>
          <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
            {errors.map((e, i) => (
              <li key={i} className="text-destructive/90 break-all">
                {e.category_url ?? e.product_url}: {e.error}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
